/**
 * <wp-calculator>
 * Wall-dimensions → pack-quantity calculator for the PDP.
 *
 * Data attributes (all set by Liquid):
 *   data-pack-qty       (int)     how many sheets/tiles per pack
 *   data-sheet-w        (float)   sheet width in inches
 *   data-sheet-h        (float)   sheet height in inches
 *   data-margin-pct     (float)   install margin (defaults to 10)
 *   data-unit           (string)  'in' or 'cm'
 *   data-category-name  (string)  category label (informational only)
 *
 * Formula (matches Karina's brief, Figma margin note):
 *   wall_area   = W × H   (inches²)
 *   with_margin = wall_area × (1 + margin_pct/100)
 *   pack_area   = pack_qty × sheet_w × sheet_h
 *   packs       = max(1, ceil(with_margin / pack_area))
 */
(function () {
  if (customElements.get('wp-calculator')) return;

  const IN_PER_CM = 1 / 2.54;

  class WPCalculator extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      this.modalId = this.dataset.modalId;
      this.packQty = Math.max(1, parseInt(this.dataset.packQty, 10) || 1);
      this.sheetW = parseFloat(this.dataset.sheetW) || 0;
      this.sheetH = parseFloat(this.dataset.sheetH) || 0;
      this.marginPct = parseFloat(this.dataset.marginPct);
      if (!Number.isFinite(this.marginPct) || this.marginPct < 0) this.marginPct = 10;
      this.unit = (this.dataset.unit || 'in').toLowerCase();
      this.categoryName = this.dataset.categoryName || '';

      this.modal = this.querySelector('#' + CSS.escape(this.modalId));
      this.form = this.modal && this.modal.querySelector('[data-wp-calc-form]');
      this.result = this.modal && this.modal.querySelector('[data-wp-calc-result]');
      this.packText = this.modal && this.modal.querySelector('[data-wp-calc-pack-text]');
      this.recText = this.modal && this.modal.querySelector('[data-wp-calc-rec-text]');
      this.applyBtn = this.modal && this.modal.querySelector('[data-wp-calc-apply]');
      // Highlighted-card CTA (cta_style "highlight"); null for the other styles.
      this.card = this.querySelector('[data-wp-calc-card]');
      this._pending = null;

      // Guard against missing DOM (bad schema / partial render)
      if (!this.modal || !this.form) return;

      // Portal the modal to <body> so it escapes any ancestor stacking context
      // (PDP containers, sticky headers, transforms, etc).
      if (this.modal.parentNode !== document.body) {
        document.body.appendChild(this.modal);
      }

      // Delegate open/close clicks inside this element AND on the portaled modal
      this.addEventListener('click', this._onClick.bind(this));
      this.modal.addEventListener('click', this._onClick.bind(this));
      this.form.addEventListener('submit', this._onSubmit.bind(this));
      document.addEventListener('keydown', this._onKeydown.bind(this));

      this._lastFocused = null;
    }

    disconnectedCallback() {
      // Clean up the portaled modal if the host is removed from the DOM
      if (this.modal && this.modal.parentNode === document.body) {
        this.modal.remove();
      }
    }

    _onClick(e) {
      if (e.target.closest('[data-wp-calc-open]')) {
        e.preventDefault();
        this.open();
      } else if (e.target.closest('[data-wp-calc-close]')) {
        e.preventDefault();
        this.close();
      } else if (e.target.closest('[data-wp-calc-apply]')) {
        e.preventDefault();
        this.applyRecommendation();
      }
    }

    applyRecommendation() {
      const packs = parseInt(this.applyBtn?.dataset.wpCalcPacks, 10);
      if (!Number.isFinite(packs) || packs < 1) return;
      this._setQuantity(packs);
      if (this._pending) this._pending.applied = true;
      this.close();
    }

    _setQuantity(packs) {
      const input = document.querySelector(
        '.m-product-form--main input[name="quantity"], form[action*="/cart/add"] input[name="quantity"]'
      );
      if (input) {
        input.value = packs;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /**
     * Swap the highlighted card to its result state, e.g.
     * "7 packs for your 120″ × 96″ wall" / "84 sheets · includes 10% extra".
     */
    _renderCard({ packs, w, h }) {
      const title = this.card.querySelector('[data-wp-calc-card-title]');
      const subtitle = this.card.querySelector('[data-wp-calc-card-subtitle]');
      const items = this.packQty * packs;
      const wDisp = this._formatDimension(w);
      const hDisp = this._formatDimension(h);
      const values = {
        packs: `${packs} ${packs === 1 ? 'pack' : 'packs'}`,
        wall: this.unit === 'in' ? `${wDisp}″ × ${hDisp}″` : `${wDisp} × ${hDisp} ${this.unit}`,
        items: `${items} ${this._itemNoun(items)}`,
        margin: this._formatNumber(this.marginPct),
      };
      const fill = (tpl) => tpl.replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));
      if (title) title.textContent = fill(this.card.dataset.resultTitle || '[packs] for your [wall] wall');
      if (subtitle) subtitle.textContent = fill(this.card.dataset.resultSubtitle || '[items] · includes [margin]% extra');
      this.card.classList.add('is-result');
    }

    /** "sheet(s)", "tile(s)", "panel(s)" or "roll(s)", from the linked Wallpaper Category name. */
    _itemNoun(count) {
      const name = this.categoryName.toLowerCase();
      let noun = 'sheet';
      if (name.includes('roll')) noun = 'roll';
      else if (name.includes('tile')) noun = 'tile';
      else if (name.includes('panel')) noun = 'panel';
      return count === 1 ? noun : `${noun}s`;
    }

    _onKeydown(e) {
      if (e.key === 'Escape' && !this.modal.hidden) this.close();
    }

    open() {
      this._lastFocused = document.activeElement;
      this.modal.hidden = false;
      document.documentElement.classList.add('wp-calc-modal-open');
      const firstInput = this.form.querySelector('input');
      if (firstInput) {
        // Defer focus until after the modal paints
        requestAnimationFrame(() => firstInput.focus());
      }
    }

    close() {
      this.modal.hidden = true;
      document.documentElement.classList.remove('wp-calc-modal-open');
      // Highlighted card: closing after a calculation shows the result on the
      // card and syncs the PDP quantity so the Add to cart total follows.
      if (this.card && this._pending) {
        if (!this._pending.applied) this._setQuantity(this._pending.packs);
        this._renderCard(this._pending);
      }
      this._pending = null;
      if (this._lastFocused && typeof this._lastFocused.focus === 'function') {
        this._lastFocused.focus();
      }
    }

    _onSubmit(e) {
      e.preventDefault();
      const w = parseFloat(this.form.width.value);
      const h = parseFloat(this.form.height.value);
      if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
        this.result.hidden = true;
        this._pending = null;
        return;
      }
      const packs = this.computePacks(w, h);
      this._pending = { packs, w, h, applied: false };
      this.renderResult(packs, w, h);
    }

    /**
     * Compute how many packs are needed for a wall of W × H (in the user's unit).
     * @param {number} w wall width
     * @param {number} h wall height
     * @returns {number} packs (>= 1)
     */
    computePacks(w, h) {
      const wIn = this.unit === 'cm' ? w * IN_PER_CM : w;
      const hIn = this.unit === 'cm' ? h * IN_PER_CM : h;
      const wallArea = wIn * hIn; // inches²
      const withMargin = wallArea * (1 + this.marginPct / 100);
      const packArea = this.packQty * this.sheetW * this.sheetH;
      if (!Number.isFinite(packArea) || packArea <= 0) return 1;
      return Math.max(1, Math.ceil(withMargin / packArea));
    }

    renderResult(packs, wallW, wallH) {
      const totalSheets = this.packQty * packs;
      const sheetWord = totalSheets === 1 ? 'sheet' : 'sheets';
      const packWord = packs === 1 ? 'pack' : 'packs';
      // Primary emphasis: appears inline within the heading line ("You'll need 1 pack").
      if (this.recText) {
        this.recText.textContent = `${packs} ${packWord}`;
      }
      // Supporting detail: total sheets + wall coverage + margin transparency.
      if (this.packText) {
        const dimsAvailable = Number.isFinite(wallW) && Number.isFinite(wallH);
        const wDisp = dimsAvailable ? this._formatDimension(wallW) : '';
        const hDisp = dimsAvailable ? this._formatDimension(wallH) : '';
        // Use prime symbol (″) for inches to avoid the "in wall" → "inside wall" misread.
        // Keep "cm" suffix at the end where it doesn't collide with prepositions.
        let wallLabel = '';
        if (dimsAvailable) {
          if (this.unit === 'in') {
            wallLabel = `covers a ${wDisp}″ × ${hDisp}″ wall`;
          } else {
            wallLabel = `covers a ${wDisp} × ${hDisp} ${this.unit} wall`;
          }
        }
        const marginLabel = this.marginPct > 0 ? `+${this._formatNumber(this.marginPct)}% margin` : '';
        const parts = [`${totalSheets} ${sheetWord}`];
        if (wallLabel && marginLabel) parts.push(`${wallLabel} ${marginLabel}`);
        else if (wallLabel) parts.push(wallLabel);
        else if (marginLabel) parts.push(marginLabel);
        this.packText.textContent = parts.join(' · ');
      }
      if (this.applyBtn) {
        this.applyBtn.dataset.wpCalcPacks = String(packs);
        this.applyBtn.textContent = `ADD ${packs} ${packWord.toUpperCase()} TO CART`;
        this.applyBtn.setAttribute(
          'aria-label',
          `Add ${packs} ${packWord} to the cart`
        );
      }
      this.result.hidden = false;
      // Scroll the result into view in case the modal is tall
      this.result.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    /**
     * Format a wall dimension. Strips trailing zeros (e.g. 100.0 → "100", 100.5 → "100.5").
     */
    _formatDimension(value) {
      return this._formatNumber(value);
    }

    _formatNumber(value) {
      if (!Number.isFinite(value)) return '';
      // Up to one decimal, drop trailing .0
      return String(Math.round(value * 10) / 10);
    }
  }

  customElements.define('wp-calculator', WPCalculator);
})();
